import { cancelEditorText } from 'owa-locstrings/lib/strings/canceleditortext.locstring.json';
import loc from 'owa-localize';
import type { ViewType } from 'owa-calendar-actions';
import { deleteCalendarEvent } from 'owa-calendar-events-actions';
import {
    lazyDeleteCalendarEventWithConfirm,
    declineCalendarEventWithConfirm,
} from 'owa-calendar-event-actions-with-confirm';
import { lazyHideItemDeleteModal, lazyShowItemDeleteModal } from 'owa-calendar-modal-actions';
import { requestModuleCommand } from 'owa-calendar-module-command';
import { getCalendarEventFromSurface } from 'owa-calendar-surface-surface-store';
import { fetchFullCalendarEvent } from 'owa-calendar-surface-full-event-locked-store';
import { openItemPeekToCancel } from 'owa-calendar-peek';
import { CalendarItemTypeConstants } from 'owa-calendar-surface-dategrid';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import EventScope from 'owa-service/lib/contract/EventScope';
import { canCancel, canDelete, canRemove } from 'owa-calendar-event-capabilities';

/**
 * Open the delete modal
 */
export function openDeleteModal(model: CalendarEvent, actionSource: ViewType) {
    openDeleteModalForCalendarEvent(model, true, actionSource);
}

/**
 * Opens the delete modal for the passed calendar event
 * @param model CalendarEvent object
 */
export function openDeleteModalForCalendarEvent(
    model: CalendarEvent,
    resetFocusOnCancel: boolean,
    actionSource: ViewType
) {
    if (!canCancel(model) && !canDelete(model) && !canRemove(model)) {
        return;
    }

    if (model.CalendarItemType != CalendarItemTypeConstants.Single) {
        // open a separate dialog window asking if you're deleting occurrence, series, or just go back
        // from there we can open the peek cancellation window with the appropriate recurrence property
        fetchFullCalendarEvent(
            model.ItemId,
            null,
            getCalendarEventFromSurface,
            undefined /** isfetchFullSeriesMasterCalendarEventFromEventInstance */,
            {
                fetchSource: 'OpenDeleteModal',
            }
        );

        lazyShowItemDeleteModal.importAndExecute(model.ItemId.Id);
    } else if (model.IsMeeting) {
        // we open up the peek cancellation window
        if (model.IsOrganizer) {
            openItemPeekToCancel.importAndExecute(
                model,
                EventScope.ThisInstanceOnly,
                actionSource,
                loc(cancelEditorText)
            );
        } else {
            // if user tries to delete a recurring meeting invite that they were invited to, assume they want to delete
            // just the one occurrence without sending any response

            if (model.IsCancelled) {
                // Once event is cancelled, it cannot be responded with a decline but can be deleted.
                // Also this flow has to be executed when it is not a series event.
                // For series event, the top level if block will handle the delete operation by displaying a modal popup.

                deleteCalendarEvent(model, actionSource);
            } else {
                declineCalendarEventWithConfirm(
                    model.ItemId,
                    model.ResponseType,
                    EventScope.Default,
                    actionSource /** actionSource */,
                    resetFocusOnCancel ? onDeleteCancel : null /** onCancel */,
                    () => requestModuleCommand(null) /** onConfirm */
                );
            }
        }
    } else {
        lazyDeleteCalendarEventWithConfirm.importAndExecute(
            model,
            EventScope.Default,
            false /*series master*/,
            actionSource,
            false /*isInPopOutWindow*/,
            resetFocusOnCancel ? onDeleteCancel : null,
            () => requestModuleCommand(null)
        );
    }
}

function onDeleteCancel() {
    lazyHideItemDeleteModal.importAndExecute();
}
